import React, { useEffect } from 'react';
import {
  CircularProgress,
  Alert,
} from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid';
import { DataGrid, PageTitle, ListLayoutPanel, AddButton, GridEditButton } from '../../../components/common';
import { EditDuenio } from './components/EditDuenio';
import { type Duenio, type DuenioRequest } from './types/duenio';
import { useDuenioStore } from './store/duenioStore';

const DueniosPage: React.FC = () => {
  const {
    duenios,
    loading,
    error,
    selectedDuenio,
    dialogOpen,
    fetchDuenios,
    saveDuenio,
    setSelectedDuenio,
    openDialog,
    closeDialog,
    clearError
  } = useDuenioStore();

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'nombres', headerName: 'Nombres', width: 200, flex: 1 },
    { field: 'apellidos', headerName: 'Apellidos', width: 200, flex: 1 },
    { field: 'numeroDocumento', headerName: 'Nº Documento', width: 150 },
    { field: 'tipoDocumento', headerName: 'Tipo Doc.', width: 120 },
    {
      field: 'isActivo',
      headerName: 'Estado',
      width: 100,
      renderCell: (params) => (
        <span style={{ color: params.row.isActivo ? 'green' : 'red' }}>
          {params.row.isActivo ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <GridEditButton
          onClick={() => handleEdit(params.row.id)}
          ariaLabel={`Editar dueño ${params.row.nombres} ${params.row.apellidos}`}
        />
      ),
    },
  ];

  useEffect(() => {
    fetchDuenios();
  }, [fetchDuenios]);

  const handleEdit = (id: number) => {
    const duenio = duenios.find(d => d.id === id) || null;
    setSelectedDuenio(duenio);
    openDialog();
  };

  const handleCreate = () => {
    setSelectedDuenio(null);
    openDialog();
  };

  const handleClose = () => {
    closeDialog();
  };

  const handleSave = async (updatedDuenio: DuenioRequest) => {
    try {
      await saveDuenio(updatedDuenio);
    } catch (error) {
      console.error('Error saving duenio:', error);
    }
  };

  if (loading) {
    return (
      <ListLayoutPanel>
        <PageTitle
          title="Dueños"
          subtitle="Gestión de dueños"
          actions={
            <AddButton
              text="Nuevo Dueño"
              onClick={handleCreate}
            />
          }
        />
        <CircularProgress />
      </ListLayoutPanel>
    );
  }

  if (error) {
    return (
      <ListLayoutPanel>
        <PageTitle
          title="Dueños"
          subtitle="Gestión de dueños"
          actions={
            <AddButton
              text="Nuevo Dueño"
              onClick={handleCreate}
            />
          }
        />
        <Alert
          severity="error"
          onClose={clearError}
        >
          {error}
        </Alert>
      </ListLayoutPanel>
    );
  }

  return (
    <ListLayoutPanel>
      <PageTitle
        title="Dueños"
        subtitle="Gestión de dueños"
        actions={
          <AddButton
            text="Nuevo Dueño"
            onClick={handleCreate}
          />
        }
      />

      <DataGrid
        columns={columns}
        rows={duenios}
        getRowId={(row: Duenio) => row.id}
      />

      <EditDuenio
        open={dialogOpen}
        duenio={selectedDuenio}
        onClose={handleClose}
        onSave={handleSave}
        title={selectedDuenio ? "Editar Dueño" : "Nuevo Dueño"}
      />
    </ListLayoutPanel>
  );
};

export default DueniosPage;
